import { DateTime } from 'luxon';
import has from '@dojo/framework/core/has';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, destroy, v, w } from '@dojo/framework/core/vdom';
import { LoadingIndicator } from '@dojo/widgets/loading-indicator';
import { Carousel, CardsLayout } from '@holmescorp/carousel';
import { convertToBoolean } from '@holmescorp/common';
import { createNlsMiddleware, documentapi, lccstate, recommended, userstate } from '@holmescorp/lcc-state';
import { ResourceParser, resourceDitabookSelector } from '@holmescorp/resource-parser';
import { GeneralCard } from '@holmescorp/card';
import css from '../css/carousel-view.m.css';
import { studyplan } from '../middleware/studyplan';
import { ViewAssessmentsCard } from '../support/cards/assess-view-card';
const icache = createICacheMiddleware();
const factory = create({
    icache,
    theme,
    nls: createNlsMiddleware('ditabook'),
    settings: createNlsMiddleware('settings'),
    resources: createNlsMiddleware('resources'),
    lccstate,
    userstate,
    recommended,
    studyplan,
    destroy,
    documentapi
}).properties();
export const CarouselView = factory(({ id, properties, middleware: { icache, theme, nls, settings, resources, recommended, studyplan, userstate, destroy, documentapi, lccstate } }) => {
    globalThis.document.getElementById('content-main')?.classList.add('study');
    destroy(() => globalThis.document.getElementById('content-main')?.classList.remove('study'));
    const themeCss = theme.classes(css);
    const { setTitle, switchView } = properties();
    const { messages: { myStudyPlan = '(ditabook) myStudyPlan', practice_type = 'practice', studyplan_a11y_description = '[ditabook] studyplan_a11y_description', exam_countdown_title = 'Exam Countdown', exam_countdown_subtitle = 'Set the date of your exam to track your progress', exam_countdown_button_label = 'Set Date' } } = nls();
    const { format, messages: { include_additional_resources = 'false', practice_exam_threshold = '5', domain_carousel_legend_small_min_width = '540' } } = settings();
    const { messages: resourceMessages } = resources();
    const overrides = {
        '@dojo/widgets/loading-indicator': {
            linear: [themeCss.linear],
            bar: [themeCss.bar]
        }
    };
    const determineCardLayout = (useSmallHeader) => {
        if (has('use-cards-layout')) {
            const cards = studyplan.getDomains().flatMap((item) => item.cards);
            const completed = studyplan
                .getDomains()
                .reduce((completed, current) => completed + current.completed, 0);
            return [
                w(CardsLayout, {
                    width: 996,
                    xqa: `all-domains-carousel`,
                    useSmallHeader,
                    headingLevel: 3,
                    legend: format('domain_carousel_legend', { completed, total: cards.length })
                }, cards)
            ];
        }
        return studyplan.getDomains().map((item) => {
            const { domain, title, cards, completed } = item;
            let legend = format('domain_carousel_legend', { completed, total: cards.length });
            if (useSmallHeader) {
                legend = format('domain_carousel_legend_small', { completed, total: cards.length });
            }
            return w(Carousel, {
                width: 996,
                xqa: `${domain}-carousel`,
                title,
                legend,
                useSmallHeader,
                headingLevel: 3
            }, cards);
        });
    };
    const quizzes = icache.getOrSet('quizzes', async () => studyplan.getQuizzes());
    if (quizzes) {
        const spToolstate = userstate.get(userstate.path('toolstate', 'studyplan'));
        const { items: sp } = spToolstate;
        window.scrollTo(0, 0);
        const prefixCards = [];
        const postfixCards = studyplan.getExtraCards();
        const onResize = () => {
            icache.set('useSmallHeader', window.innerWidth < parseInt(domain_carousel_legend_small_min_width));
        };
        if (!icache.get('listenerAttached')) {
            window.addEventListener('resize', onResize);
            icache.set('listenerAttached', true);
        }
        const useSmallHeader = icache.getOrSet('useSmallHeader', () => window.innerWidth < parseInt(domain_carousel_legend_small_min_width));
        const domainCarousels = determineCardLayout(useSmallHeader);
        const extraCardInfo = studyplan.getExtraCardInfo();
        const additionalCarousels = convertToBoolean(include_additional_resources)
            ? w(ResourceParser, {
                messages: resourceMessages,
                topicid: 'additional_resources_carousel',
                key: `resource_parsed_arc_${postfixCards.length}`,
                formatting: extraCardInfo,
                preformat: true
            })
            : null;
        const tgfCards = studyplan.getStudyPlan(sp);
        const { get, path } = userstate;
        if (!recommended.assessmentsComplete()) {
            const assessViewCard = w(ViewAssessmentsCard, { xqa: 'view-assessments-card', switchView, setTitle });
            prefixCards.unshift(assessViewCard);
        }
        else {
            prefixCards.push(w(ResourceParser, { messages: resourceMessages, topicid: 'pretestReviewCard' }));
            const { allcompleted } = recommended.tracked;
            const completed = allcompleted.every((item) => item);
            if (has('posttest') && completed) {
                prefixCards.push(w(ResourceParser, { messages: resourceMessages, topicid: 'postTestCard' }));
            }
        }
        const examThreshold = parseInt(practice_exam_threshold) || 14;
        const examdate = get(path('examdate'));
        const duration = DateTime.fromSQL(examdate).diff(DateTime.now(), 'days').toObject();
        const daysUntilExam = Math.ceil(duration.days);
        if (examdate && daysUntilExam < examThreshold) {
            if (has('posttest') && practice_type === 'posttest') {
                prefixCards.push(w(ResourceParser, { messages: resourceMessages, topicid: 'postTestCard' }));
            }
            else if (has('practice-exam') && practice_type === 'practice') {
                prefixCards.push(w(ResourceParser, { messages: resourceMessages, topicid: 'practiceCard' }));
            }
            if ('examDayToolsCard' in resourceMessages) {
                const examDayToolsCard = w(ResourceParser, {
                    messages: resourceMessages,
                    topicid: 'examDayToolsCard'
                });
                prefixCards.push(examDayToolsCard);
            }
        }
        const product = lccstate.get(lccstate.path('product'));
        const landingState = userstate.get(userstate.path('toolstate', 'landing'));
        const hasExam = landingState.has_exam;
        if (hasExam && !examdate && has('use-exam-reminder')) {
            prefixCards.push(w(GeneralCard, {
                type: 'event',
                action: 'settings/open',
                title: exam_countdown_title,
                mediaSrc: `${window.hcstatic.portal_cdn}/js/images/${product}/card_header_exam_countdown.png`,
                mediaTitle: exam_countdown_title,
                subtitle: exam_countdown_subtitle,
                button_label: exam_countdown_button_label,
                ispromoted: 'true'
            }));
        }
        const motivationCards = studyplan.getMotivations();
        const spCards = [...prefixCards, ...motivationCards, ...tgfCards, ...postfixCards];
        documentapi.setTitle(studyplan_a11y_description);
        return v('div', { id, key: 'carousel-view', xqa: 'carousel-view', classes: [themeCss.root] }, [
            v('div', { classes: [themeCss.studyplan_carousel] }, [
                w(resourceDitabookSelector, {
                    topic: 'msp_desc',
                    key: 'msp-intro',
                    classes: [themeCss.msp_desc]
                }),
                w(Carousel, {
                    width: 996,
                    key: `studyplan-${examdate}`,
                    xqa: 'studyPlanCarousel',
                    seatsVisible: 4,
                    title: myStudyPlan,
                    headingLevel: 2
                }, spCards)
            ]),
            w(resourceDitabookSelector, {
                topic: 'studyplan_instructions',
                classes: [themeCss.studyplan_instructions]
            }),
            v('div', { classes: [themeCss.domain_carousels] }, [...domainCarousels, additionalCarousels])
        ]);
    }
    return w(LoadingIndicator, { classes: overrides, key: 'studyplan-loading' });
});
export default CarouselView;
